import { motion } from 'motion/react';
import { useInView } from '../../../components/hooks/useInView.jsx';
import { CheckCircle } from 'lucide-react';

export function OutcomeSection() {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  const outcomes = [
    'Holistic health solution combining fitness, nutrition, and sleep tracking',
    'User-friendly interface designed for all fitness levels',
    'Personalized recommendations powered by user preferences',
    'Engaging progress visualizations to maintain motivation'
  ];

  return (
    <section
      ref={ref}
      className="min-h-screen flex items-center py-20 px-6 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-radial from-[#fd5b38]/5 via-transparent to-transparent" />

      <div className="max-w-7xl mx-auto w-full relative z-10 ">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-6xl md:text-8xl mb-8 tracking-tight">Outcome</h2>
          <div className="h-1 w-20 bg-[#fd5b38] mx-auto mb-8" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-16"
        >
          <p className="text-2xl md:text-3xl text-gray-300 text-center max-w-4xl mx-auto leading-relaxed mb-12">
            This conceptual fitness tracker app successfully addresses the
            fragmentation in wellness solutions by creating a unified platform
            that empowers users to achieve their health goals.
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {outcomes.map((outcome, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="flex items-start gap-4 bg-[#1a1a1a]/50 p-6 rounded-2xl item-border border-gray-800 hover:border-[#fd5b38]/50 transition-all duration-300"
              >
                <CheckCircle className="w-6 h-6 text-[#fd5b38] flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-300">{outcome}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="text-center"
        >
          <h3 className="text-4xl md:text-6xl mb-8 leading-tight">
            Conclusion
          </h3>

          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            By focusing on user needs and creating a seamless experience across
            fitness, nutrition, and recovery, this app concept demonstrates how
            thoughtful UX design can transform wellness journeys. The
            integration of personalized insights, intuitive interfaces, and
            engaging progress tracking creates a foundation for long-term user
            retention and success.
          </p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-20 pt-12 border-t border-gray-800"
          >
            <p className="text-gray-500 mb-4">My Role</p>
            <div className="flex items-center justify-center gap-8 flex-wrap">
              <span className="text-[#fd5b38] text-lg">UI Designer</span>
              <span className="text-gray-600">•</span>
              <span className="text-[#fd5b38] text-lg">UX Designer</span>
              <span className="text-gray-600">•</span>
              <span className="text-[#fd5b38] text-lg">UX Researcher</span>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 0.3 } : {}}
          transition={{ duration: 1, delay: 1.5 }}
          className="text-center"
        >
          <p className="text-sm text-white mt-8">
            Thank you for exploring this case study
          </p>
        </motion.div>
      </div>
    </section>
  );
}
